import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { TenantLlmConfigViewStore } from "../../store";
import { TenantLlmConfigView } from "../view/TenantLlmConfigView";
import { TenantLlmConfigUpdateStore } from "../../store";
import { TenantLlmConfigUpdate } from "../update/TenantLlmConfigUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type TenantLlmConfigModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const TenantLlmConfigModalContent: React.FC<TenantLlmConfigModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new TenantLlmConfigUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new TenantLlmConfigViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <TenantLlmConfigView
            tenantLlmConfigViewStore={
              storeInfo.store as TenantLlmConfigViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <TenantLlmConfigUpdate
            tenantLlmConfigUpdateStore={
              storeInfo.store as TenantLlmConfigUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
